import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AdminContext } from '../../../context/admin-context';
import EventsList from '../../components/admin/events/events-list';
import { ADMIN_NAV_GROUPS, getAdminNavItemActive } from './admin-nav-config';
import { AdminNavIcon } from './admin-nav-icon';
import './admin-nav-style.css';

const ICON_SIZE = 20;
const FAB_ICON_SIZE = 22;

function AdminNavLink({ item, onNavigate }) {
  return (
    <NavLink
      to={item.to}
      exact={item.exact}
      isActive={(_match, location) => getAdminNavItemActive(location.pathname, item)}
      className="admin-nav-item"
      activeClassName="is-active"
      title={item.label}
      onClick={onNavigate}
    >
      <span className="admin-nav-item-icon">
        <AdminNavIcon name={item.icon} size={ICON_SIZE} />
      </span>
      <span className="admin-nav-item-label">{item.label}</span>
    </NavLink>
  );
}

function AdminNav() {
  const { logout } = useContext(AdminContext);
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [panelHeight, setPanelHeight] = useState(null);
  const [panelTop, setPanelTop] = useState(null);
  const panelRef = useRef(null);
  const shellRef = useRef(null);

  useEffect(() => {
    setOpen(false);
    setEventsOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) {
      setEventsOpen(false);
      setPanelHeight(null);
      setPanelTop(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !panelRef.current) {
      return undefined;
    }

    const node = panelRef.current;
    const syncPanelMetrics = () => {
      const rect = node.getBoundingClientRect();
      setPanelHeight(rect.height);
      setPanelTop(rect.top);
    };

    syncPanelMetrics();
    const observer = new ResizeObserver(syncPanelMetrics);
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [open]);

  useEffect(() => {
    if (!open || !eventsOpen || !panelRef.current) {
      return;
    }

    const rect = panelRef.current.getBoundingClientRect();
    setPanelHeight(rect.height);
    setPanelTop(rect.top);
  }, [open, eventsOpen]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      if (eventsOpen) {
        setEventsOpen(false);
      } else {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, eventsOpen]);

  const closeEvents = useCallback(() => {
    setEventsOpen(false);
  }, []);

  const closePanel = () => {
    setEventsOpen(false);
    setOpen(false);
  };

  const togglePanel = () => {
    setOpen((prev) => {
      if (prev) {
        setEventsOpen(false);
      }
      return !prev;
    });
  };

  const handleLogout = () => {
    closePanel();
    logout();
  };

  return (
    <div className="admin-nav-shell" ref={shellRef}>
      <button
        type="button"
        className={'admin-nav-fab' + (open ? ' is-open' : '')}
        onClick={togglePanel}
        aria-expanded={open}
        aria-controls="admin-nav-panel"
        aria-label={open ? 'بستن منوی مدیر' : 'باز کردن منوی مدیر'}
      >
        <AdminNavIcon name={open ? 'close' : 'menu'} size={FAB_ICON_SIZE} className="admin-nav-fab-icon" />
      </button>

      {open && <button type="button" className="admin-nav-backdrop" aria-label="بستن منو" onClick={closePanel} />}

      <nav
        id="admin-nav-panel"
        ref={panelRef}
        className={'admin-nav-panel' + (open ? ' is-open' : '')}
        aria-label="منوی مدیر"
        aria-hidden={!open}
      >
        <div className="admin-nav-panel-header">
          <span className="admin-nav-panel-title">منوی مدیر</span>
          <button type="button" className="admin-nav-panel-close" onClick={closePanel} aria-label="بستن">
            <AdminNavIcon name="close" size={18} className="admin-nav-panel-close-icon" />
          </button>
        </div>

        <div className="admin-nav-panel-body">
          <section className="admin-nav-group admin-nav-group--events" aria-label="رویدادها">
            <EventsList
              menuOpen={open}
              eventsOpen={eventsOpen}
              panelHeight={panelHeight}
              panelTop={panelTop}
              portalRoot={shellRef}
              onToggle={() => setEventsOpen((prev) => !prev)}
              onClose={closeEvents}
              onNavigate={closePanel}
            />
          </section>

          {ADMIN_NAV_GROUPS.map((group) => (
            <section className="admin-nav-group" key={group.id} aria-label={group.label}>
              <h2 className="admin-nav-group-heading">{group.label}</h2>
              <div className="admin-nav-group-items">
                {group.items.map((item) => (
                  <AdminNavLink key={item.to} item={item} onNavigate={closePanel} />
                ))}
              </div>
            </section>
          ))}

          <section className="admin-nav-group admin-nav-group--account" aria-label="حساب">
            <button type="button" className="admin-nav-item admin-nav-logout" onClick={handleLogout} title="خروج از پنل">
              <span className="admin-nav-item-icon">
                <AdminNavIcon name="lockOpen" size={ICON_SIZE} />
              </span>
              <span className="admin-nav-item-label">خروج</span>
            </button>
          </section>
        </div>
      </nav>
    </div>
  );
}

export default AdminNav;
